// src/features/documents/components/DocumentsTable.tsx
import { useState, useMemo, useCallback, useRef } from 'react';
import {
  FileText, Image, Film, Music, FileSpreadsheet, File,
  ChevronLeft, ChevronRight, Search, X, Eye, ThumbsUp, ThumbsDown,
  ArrowUp, ArrowDown,
} from 'lucide-react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from './StatusBadge';
import { ColumnSettings } from './ColumnSettings';
import { sanitizeHtml } from '../api';
import { useTags } from '../hooks/useDocuments';
import type { Document, ColumnDef, ColumnId, TagInfo } from '../types';
import { formatBytes } from '@/lib/utils';

// --- Helpers ---

function getFileIcon(mime: string | null) {
  if (!mime) return File;
  if (mime.startsWith('image/')) return Image;
  if (mime.startsWith('video/')) return Film;
  if (mime.startsWith('audio/')) return Music;
  if (mime.includes('spreadsheet') || mime.includes('csv') || mime.includes('excel')) return FileSpreadsheet;
  if (mime.includes('pdf') || mime.includes('text')) return FileText;
  return File;
}

function formatDate(date: string | null): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-CA');
}

function stripAccents(s: string) {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function shortenMime(mime: string | null): string {
  if (!mime) return '—';
  const short = mime.split('/').pop()
    ?.replace('vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'xlsx')
    .replace('vnd.ms-excel', 'xls')
    .replace('plain', 'txt')
    .replace('jpeg', 'jpg')
    .toUpperCase();
  return short ?? '—';
}

// --- Column definitions ---

const DEFAULT_COLUMNS: ColumnDef[] = [
  { id: 'name', label: 'Name' },
  { id: 'originalName', label: 'Original Name', hidden: true },
  { id: 'date', label: 'Doc Date', width: 'w-28' },
  { id: 'mimeType', label: 'Type', width: 'w-24' },
  { id: 'size', label: 'Size', width: 'w-24', align: 'right' },
  { id: 'status', label: 'Status', width: 'w-24', align: 'center' },
  { id: 'tags', label: 'Tags', width: 'w-48' },
  { id: 'content', label: 'Content', width: 'w-60' },
  { id: 'pages', label: 'Pages', width: 'w-16', align: 'right', hidden: true },
  { id: 'words', label: 'Words', width: 'w-20', align: 'right', hidden: true },
  { id: 'language', label: 'Lang', width: 'w-16', align: 'center', hidden: true },
  { id: 'source', label: 'Source', width: 'w-20', align: 'center', hidden: true },
  { id: 'sourcePath', label: 'Source Path', hidden: true },
  { id: 'version', label: 'Ver', width: 'w-14', align: 'center', hidden: true },
  { id: 'created', label: 'Created', width: 'w-28', hidden: true },
  { id: 'updated', label: 'Updated', width: 'w-28' },
  { id: 'views', label: 'Views', width: 'w-16', align: 'right' },
  { id: 'reactions', label: 'Reactions', width: 'w-24', align: 'center' },
];

// --- Column preferences (localStorage) ---

const COL_PREFS_KEY = 'atek_dms_columns';

function loadColumnPrefs(): { order: ColumnId[]; hidden: Set<ColumnId> } {
  try {
    const raw = localStorage.getItem(COL_PREFS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const savedOrder: ColumnId[] = parsed.order;
      const allIds = DEFAULT_COLUMNS.map((c) => c.id);
      const missing = allIds.filter((id) => !savedOrder.includes(id));
      return { order: [...savedOrder, ...missing], hidden: new Set(parsed.hidden) };
    }
  } catch { /* ignore */ }
  return {
    order: DEFAULT_COLUMNS.map((c) => c.id),
    hidden: new Set(DEFAULT_COLUMNS.filter((c) => c.hidden).map((c) => c.id)),
  };
}

function saveColumnPrefs(order: ColumnId[], hidden: Set<ColumnId>) {
  localStorage.setItem(COL_PREFS_KEY, JSON.stringify({ order, hidden: [...hidden] }));
}

// --- Column widths (localStorage) ---

const COL_WIDTHS_KEY = 'atek_dms_col_widths';

function loadColumnWidths(): Record<string, number> {
  try {
    const raw = localStorage.getItem(COL_WIDTHS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveColumnWidths(widths: Record<string, number>) {
  localStorage.setItem(COL_WIDTHS_KEY, JSON.stringify(widths));
}

// --- Tag rendering ---

function TagBadges({ tagIds, tagsMap }: { tagIds: string[]; tagsMap: Map<string, TagInfo> }) {
  if (tagIds.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {tagIds.map((id) => {
        const tag = tagsMap.get(id);
        const color = tag?.color || tag?.group?.color || '#94a3b8';
        return (
          <span
            key={id}
            className="inline-block px-1.5 py-px rounded text-[10px] font-medium whitespace-nowrap"
            style={{
              backgroundColor: `${color}20`,
              color,
              border: `1px solid ${color}40`,
            }}
          >
            {tag?.label ?? id.slice(0, 8)}
          </span>
        );
      })}
    </div>
  );
}

// --- Filter rendering ---

function renderFilter(
  id: ColumnId,
  filters: Record<string, string>,
  setFilter: (id: string, value: string) => void,
  tags: Map<string, TagInfo>,
) {
  // Date range columns
  if (id === 'date' || id === 'created' || id === 'updated') {
    const fromKey = id === 'date' ? 'date_from' : `${id}_from`;
    const toKey = id === 'date' ? 'date_to' : `${id}_to`;
    return (
      <div className="flex flex-col gap-0.5">
        <Input type="date" value={filters[fromKey] ?? ''} onChange={(e) => setFilter(fromKey, e.target.value)} className="h-6 text-xs px-1" title="From" />
        <Input type="date" value={filters[toKey] ?? ''} onChange={(e) => setFilter(toKey, e.target.value)} className="h-6 text-xs px-1" title="To" />
      </div>
    );
  }
  // Select filters
  if (id === 'status') {
    return (
      <select value={filters.status ?? ''} onChange={(e) => setFilter('status', e.target.value)} className="h-7 w-full text-xs border rounded px-1 bg-background">
        <option value="">All</option>
        <option value="done">done</option>
        <option value="processing">processing</option>
        <option value="pending">pending</option>
        <option value="failed">failed</option>
        <option value="skipped">skipped</option>
      </select>
    );
  }
  if (id === 'mimeType') {
    return (
      <select value={filters.mimeType ?? ''} onChange={(e) => setFilter('mimeType', e.target.value)} className="h-7 w-full text-xs border rounded px-1 bg-background">
        <option value="">All</option>
        <option value="pdf">PDF</option>
        <option value="image/">Image</option>
        <option value="spreadsheet">Spreadsheet</option>
        <option value="text/">Text</option>
      </select>
    );
  }
  if (id === 'language') {
    return (
      <select value={filters.language ?? ''} onChange={(e) => setFilter('language', e.target.value)} className="h-7 w-full text-xs border rounded px-1 bg-background">
        <option value="">All</option>
        <option value="en">EN</option>
        <option value="fr">FR</option>
      </select>
    );
  }
  if (id === 'source') {
    return (
      <select value={filters.source ?? ''} onChange={(e) => setFilter('source', e.target.value)} className="h-7 w-full text-xs border rounded px-1 bg-background">
        <option value="">All</option>
        <option value="ui">UI</option>
        <option value="cli">CLI</option>
        <option value="api">API</option>
        <option value="bulk">Bulk</option>
      </select>
    );
  }
  if (id === 'tags') {
    // Collect unique tag labels from the tags map for a dropdown
    const tagLabels = Array.from(tags.values()).map((t) => t.label).sort();
    if (tagLabels.length > 0) {
      return (
        <select value={filters.tags ?? ''} onChange={(e) => setFilter('tags', e.target.value)} className="h-7 w-full text-xs border rounded px-1 bg-background">
          <option value="">All</option>
          {tagLabels.map((label) => <option key={label} value={label}>{label}</option>)}
        </select>
      );
    }
  }
  // Skip filter for reactions (not very useful)
  if (id === 'reactions') return null;
  // Text filter for everything else
  return (
    <Input
      type="text"
      placeholder="..."
      value={filters[id] ?? ''}
      onChange={(e) => setFilter(id, e.target.value)}
      className="h-7 text-xs"
    />
  );
}

// --- Main component ---

interface DocumentsTableProps {
  documents: Document[];
  semanticResults: Document[];
  semanticLoading: boolean;
  isLoading: boolean;
  pageIndex: number;
  hasMore: boolean;
  searchQuery: string;
  searchInput: string;
  selectedDoc: Document | null;
  onSelectDoc: (doc: Document | null) => void;
  onSearchInputChange: (value: string) => void;
  onSearch: (e: React.FormEvent) => void;
  onClearSearch: () => void;
  onPrevPage: () => void;
  onNextPage: () => void;
}

export function DocumentsTable({
  documents,
  semanticResults,
  semanticLoading,
  isLoading,
  pageIndex,
  hasMore,
  searchQuery,
  searchInput,
  selectedDoc,
  onSelectDoc,
  onSearchInputChange,
  onSearch,
  onClearSearch,
  onPrevPage,
  onNextPage,
}: DocumentsTableProps) {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sortKey, setSortKey] = useState<ColumnId | null>(null);
  const [sortDir, setSortDir] = useState<1 | -1>(1);
  const [colPrefs, setColPrefs] = useState(loadColumnPrefs);
  const [colWidths, setColWidths] = useState<Record<string, number>>(loadColumnWidths);
  const resizing = useRef(false);

  const handleColResize = useCallback((colId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = true;
    const startX = e.clientX;
    const th = (e.target as HTMLElement).closest('th');
    const startWidth = th?.offsetWidth ?? 100;

    const onMove = (ev: MouseEvent) => {
      if (!resizing.current) return;
      const delta = ev.clientX - startX;
      const newWidth = Math.max(40, startWidth + delta);
      setColWidths((prev) => ({ ...prev, [colId]: newWidth }));
    };

    const onUp = () => {
      resizing.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setColWidths((prev) => { saveColumnWidths(prev); return prev; });
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  const { data: tagsMap } = useTags();
  const tags = tagsMap ?? new Map<string, TagInfo>();

  const setFilter = useCallback((id: string, value: string) => {
    setFilters((prev) => ({ ...prev, [id]: value }));
  }, []);

  const handleSort = useCallback((id: ColumnId) => {
    setSortKey((prev) => {
      if (prev === id) {
        if (sortDir === 1) { setSortDir(-1); return id; }
        // Already descending → clear sort
        setSortDir(1);
        return null;
      }
      setSortDir(1);
      return id;
    });
  }, [sortDir]);

  const visibleColumns = useMemo(
    () => colPrefs.order
      .map((id) => DEFAULT_COLUMNS.find((c) => c.id === id)!)
      .filter((c) => c && !colPrefs.hidden.has(c.id)),
    [colPrefs],
  );

  const orderedColumns = colPrefs.order.map((id) => DEFAULT_COLUMNS.find((c) => c.id === id)!);

  // Extract a sortable/filterable raw value for a column
  function getRawValue(col: ColumnId, doc: Document): string | number | null {
    const v = doc.currentVersion;
    switch (col) {
      case 'name': return doc.name;
      case 'originalName': return v?.originalName ?? null;
      case 'date': return doc.documentDate;
      case 'mimeType': return shortenMime(v?.sourceMimeType ?? null);
      case 'size': return v?.sourceSize ?? null;
      case 'status': return v?.conversionStatus ?? null;
      case 'tags': return (doc.tagIds ?? []).map((id) => tags.get(id)?.label ?? '').join(' ');
      // The list payload carries a bounded preview, never the body
      // (ATE-12094 review, R4): this is the value the column sorts and
      // filters on, so it must come from `contentPreview`.
      case 'content': return v?.contentPreview ?? null;
      case 'pages': return v?.pageCount ?? null;
      case 'words': return v?.wordCount ?? null;
      case 'language': return v?.language ?? null;
      case 'source': return v?.uploadSource ?? null;
      case 'sourcePath': return v?.sourcePath ?? null;
      case 'version': return v?.version ?? null;
      case 'created': return doc.createdAt;
      case 'updated': return doc.updatedAt;
      case 'views': return doc.viewCount;
      case 'reactions': return doc.thumbsUp - doc.thumbsDown;
      default: return null;
    }
  }

  const filteredDocuments = useMemo(() => {
    let result = documents.filter((doc) => {
      for (const [key, val] of Object.entries(filters)) {
        if (!val) continue;
        // Date range filters use special keys
        if (key === 'date_from') {
          if (doc.documentDate && doc.documentDate < val) return false;
          continue;
        }
        if (key === 'date_to') {
          if (doc.documentDate && doc.documentDate > val) return false;
          continue;
        }
        if (key === 'created_from') {
          if (doc.createdAt < val) return false;
          continue;
        }
        if (key === 'created_to') {
          if (doc.createdAt > val) return false;
          continue;
        }
        if (key === 'updated_from') {
          if (doc.updatedAt < val) return false;
          continue;
        }
        if (key === 'updated_to') {
          if (doc.updatedAt > val) return false;
          continue;
        }
        // Select filters: exact match
        if (key === 'status' || key === 'language' || key === 'source') {
          const raw = getRawValue(key as ColumnId, doc);
          if (String(raw ?? '') !== val) return false;
          continue;
        }
        // Type filter: partial match on mime
        if (key === 'mimeType') {
          const mime = doc.currentVersion?.sourceMimeType ?? '';
          if (!mime.toLowerCase().includes(val.toLowerCase())) return false;
          continue;
        }
        // Text filters: case-insensitive substring
        const raw = getRawValue(key as ColumnId, doc);
        const rawStr = raw != null ? String(raw) : '';
        if (!stripAccents(rawStr.toLowerCase()).includes(stripAccents(val.toLowerCase()))) return false;
      }
      return true;
    });

    // Sort
    if (sortKey) {
      result = [...result].sort((a, b) => {
        const av = getRawValue(sortKey, a);
        const bv = getRawValue(sortKey, b);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sortDir;
        return String(av).localeCompare(String(bv)) * sortDir;
      });
    }

    return result;
  }, [documents, filters, sortKey, sortDir, tags]);

  const handleToggleCol = (id: ColumnId) => {
    const next = new Set(colPrefs.hidden);
    if (next.has(id)) next.delete(id); else next.add(id);
    setColPrefs((p) => { saveColumnPrefs(p.order, next); return { ...p, hidden: next }; });
  };

  const handleMoveCol = (id: ColumnId, dir: -1 | 1) => {
    const order = [...colPrefs.order];
    const idx = order.indexOf(id);
    if (idx < 0) return;
    const target = idx + dir;
    if (target < 0 || target >= order.length) return;
    [order[idx], order[target]] = [order[target], order[idx]];
    setColPrefs((p) => { saveColumnPrefs(order, p.hidden); return { ...p, order }; });
  };

  function renderCellValue(col: ColumnDef, doc: Document) {
    const v = doc.currentVersion;
    switch (col.id) {
      case 'name': return (
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-foreground">{doc.name}</span>
            {searchQuery && doc.matchType && (
              <span className={`inline-block px-1 py-px rounded text-[9px] font-medium leading-tight ${
                doc.matchType === 'semantic' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                : doc.matchType === 'content' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
              }`}>
                {doc.matchType === 'semantic' ? 'passage' : doc.matchType === 'content' ? 'content' : 'title'}
              </span>
            )}
          </div>
        );
      case 'originalName': return <span className="text-muted-foreground">{v?.originalName ?? '—'}</span>;
      case 'date': return <span className="text-muted-foreground">{formatDate(doc.documentDate)}</span>;
      case 'mimeType': return <span className="font-mono text-xs text-muted-foreground">{shortenMime(v?.sourceMimeType ?? null)}</span>;
      case 'size': return <span className="font-mono text-xs text-muted-foreground">{v?.sourceSize ? formatBytes(v.sourceSize) : '—'}</span>;
      case 'status': return <StatusBadge status={v?.conversionStatus ?? null} />;
      case 'tags': return <TagBadges tagIds={doc.tagIds ?? []} tagsMap={tags} />;
      case 'content': {
        // When searching: show search evidence (headline with highlights, or matched chunk)
        if (searchQuery) {
          if (doc.matchedChunk?.text) {
            return (
              <div className="text-xs leading-tight line-clamp-2">
                {doc.matchedChunk.heading && (
                  <span className="font-medium text-foreground/70">{doc.matchedChunk.heading} · </span>
                )}
                <span className="text-muted-foreground">{doc.matchedChunk.text.slice(0, 150)}</span>
                {doc.matchedChunk.pageNumbers && doc.matchedChunk.pageNumbers.length > 0 && (
                  <span className="text-blue-500 ml-1">p.{doc.matchedChunk.pageNumbers.join(',')}</span>
                )}
              </div>
            );
          }
          if (doc.searchHeadline) {
            return (
              <span
                className="text-muted-foreground text-xs leading-tight line-clamp-2 [&_mark]:bg-yellow-200 [&_mark]:dark:bg-yellow-800 [&_mark]:rounded-sm [&_mark]:px-0.5"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(doc.searchHeadline) }}
              />
            );
          }
        }
        // Default: first 120 chars of content
        // List rows carry the bounded preview on the version; search rows
        // carry theirs at row level (`LEFT(..., 3000)`). Neither carries the
        // body (ATE-12094 review, R4).
        const text = v?.contentPreview ?? doc.contentPreview;
        if (!text) return <span className="text-muted-foreground">—</span>;
        const preview = text.replace(/^#.*$/gm, '').replace(/\n+/g, ' ').trim().slice(0, 120);
        return (
          <span className="text-muted-foreground text-xs leading-tight line-clamp-2" title={text.slice(0, 500)}>
            {preview}{text.length > 120 ? '...' : ''}
          </span>
        );
      }
      case 'pages': return <span className="font-mono text-xs text-muted-foreground">{v?.pageCount ?? '—'}</span>;
      case 'words': return <span className="font-mono text-xs text-muted-foreground">{v?.wordCount != null ? v.wordCount.toLocaleString() : '—'}</span>;
      case 'language': return <span className="font-mono text-xs uppercase text-muted-foreground">{v?.language ?? '—'}</span>;
      case 'source': return <span className="font-mono text-xs text-muted-foreground">{v?.uploadSource ?? '—'}</span>;
      case 'sourcePath': return <span className="text-muted-foreground text-xs truncate max-w-[200px] inline-block" title={v?.sourcePath ?? ''}>{v?.sourcePath ?? '—'}</span>;
      case 'version': return <span className="font-mono text-xs text-muted-foreground">{v?.version ?? '—'}</span>;
      case 'created': return <span className="text-muted-foreground">{formatDate(doc.createdAt)}</span>;
      case 'updated': return <span className="text-muted-foreground">{formatDate(doc.updatedAt)}</span>;
      case 'views': return (
        <span className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground">
          <Eye className="h-3 w-3" />{doc.viewCount}
        </span>
      );
      case 'reactions': return (
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-0.5 text-green-600">
            <ThumbsUp className="h-3 w-3" />{doc.thumbsUp}
          </span>
          <span className="inline-flex items-center gap-0.5 text-red-500">
            <ThumbsDown className="h-3 w-3" />{doc.thumbsDown}
          </span>
        </span>
      );
    }
  }

  return (
    <>
      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-2">
        <form onSubmit={onSearch} className="flex-1 flex items-center gap-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search documents..."
              value={searchInput}
              onChange={(e) => onSearchInputChange(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={onClearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </form>
        <ColumnSettings
          columns={orderedColumns}
          hidden={colPrefs.hidden}
          onToggle={handleToggleCol}
          onMove={handleMoveCol}
        />
      </div>

      {searchQuery && !isLoading && (
        <p className="text-xs text-muted-foreground mb-2">
          {filteredDocuments.length} keyword match{filteredDocuments.length !== 1 ? 'es' : ''}
        </p>
      )}

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table style={{ tableLayout: 'fixed' }}>
          <TableHeader>
            {/* Filter row */}
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead className="p-1 w-9" />
              {visibleColumns.map((col) => (
                <TableHead
                  key={`filter-${col.id}`}
                  className="p-1"
                  style={colWidths[col.id] ? { width: colWidths[col.id], minWidth: colWidths[col.id] } : undefined}
                >
                  {renderFilter(col.id, filters, setFilter, tags)}
                </TableHead>
              ))}
            </TableRow>
            {/* Column labels — clickable to sort, with resize handles */}
            <TableRow className="bg-muted/50">
              <TableHead className="w-9" />
              {visibleColumns.map((col) => (
                <TableHead
                  key={col.id}
                  className={`relative select-none cursor-pointer hover:bg-muted/80 ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''}`}
                  style={colWidths[col.id] ? { width: colWidths[col.id], minWidth: colWidths[col.id] } : undefined}
                  onClick={() => handleSort(col.id)}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {sortKey === col.id && (
                      sortDir === 1
                        ? <ArrowUp className="h-3 w-3 text-foreground" />
                        : <ArrowDown className="h-3 w-3 text-foreground" />
                    )}
                  </span>
                  <div
                    className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-blue-400/50 active:bg-blue-500/50"
                    onMouseDown={(e) => { e.stopPropagation(); handleColResize(col.id, e); }}
                  />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={visibleColumns.length + 1}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : filteredDocuments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length + 1} className="text-center text-muted-foreground py-10">
                  No documents found
                </TableCell>
              </TableRow>
            ) : (
              filteredDocuments.map((doc) => {
                const mime = doc.currentVersion?.sourceMimeType ?? null;
                const IconComponent = getFileIcon(mime);
                const isSelected = selectedDoc?.id === doc.id;
                return (
                  <TableRow
                    key={doc.id}
                    onClick={() => onSelectDoc(isSelected ? null : doc)}
                    className={`cursor-pointer ${isSelected ? 'bg-blue-50 dark:bg-blue-950/20' : 'hover:bg-muted/50'}`}
                  >
                    <TableCell className="text-center">
                      <IconComponent className="h-4 w-4 text-muted-foreground inline-block" />
                    </TableCell>
                    {visibleColumns.map((col) => (
                      <TableCell
                        key={col.id}
                        className={col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''}
                      >
                        {renderCellValue(col, doc)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Semantic results — "Related passages" section */}
      {searchQuery && (semanticLoading || semanticResults.length > 0) && (
        <div className="mt-4">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            {semanticLoading ? 'Searching related passages...' : `${semanticResults.length} related passage${semanticResults.length !== 1 ? 's' : ''}`}
          </p>
          {semanticLoading ? (
            <div className="border rounded-lg p-4 space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-3/4" />
            </div>
          ) : (
            <div className="border rounded-lg divide-y">
              {semanticResults.map((doc) => {
                const mime = doc.currentVersion?.sourceMimeType ?? null;
                const IconComponent = getFileIcon(mime);
                const isSelected = selectedDoc?.id === doc.id;
                return (
                  <div
                    key={doc.id}
                    onClick={() => onSelectDoc(isSelected ? null : doc)}
                    className={`flex items-start gap-3 p-3 cursor-pointer ${isSelected ? 'bg-blue-50 dark:bg-blue-950/20' : 'hover:bg-muted/50'}`}
                  >
                    <IconComponent className="h-4 w-4 text-muted-foreground mt-1 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-sm text-foreground truncate">{doc.name}</span>
                        <span className="inline-block px-1 py-px rounded text-[9px] font-medium leading-tight bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                          passage
                        </span>
                      </div>
                      {doc.matchedChunk && (
                        <div className="mt-1 text-xs leading-relaxed">
                          <div className="text-muted-foreground/70 mb-0.5">
                            {doc.matchedChunk.heading && <span className="font-medium">{doc.matchedChunk.heading}</span>}
                            {doc.matchedChunk.pageNumbers && doc.matchedChunk.pageNumbers.length > 0 && (
                              <span className="text-blue-500 ml-1.5">p.{doc.matchedChunk.pageNumbers.join(',')}</span>
                            )}
                          </div>
                          <p className="text-muted-foreground line-clamp-2">{doc.matchedChunk.text.slice(0, 200)}</p>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground shrink-0 mt-1">
                      {doc.documentDate ? new Date(doc.documentDate).toLocaleDateString('en-CA') : '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && filteredDocuments.length > 0 && (
        <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
          <span>
            Page {pageIndex + 1} · {filteredDocuments.length} documents
            {Object.values(filters).some(Boolean) && ' (filtered)'}
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={pageIndex === 0}
              onClick={onPrevPage}
              className="h-7 text-xs gap-1"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasMore}
              onClick={onNextPage}
              className="h-7 text-xs gap-1"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

    </>
  );
}
